import { useState, useCallback, useEffect } from "react";
import axios, { all } from "axios";
import { toast } from "react-toastify";
import { CORE_API_BASE_URL } from "constants";

const API_BASE_URL = `${CORE_API_BASE_URL}/clients`;

export default function useClientIDs() {
  const authToken = sessionStorage.getItem("authToken");
  const [clientIDs, setClientIDs] = useState([]);
  const [selectedID, setSelectedID] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // handle axios errors
  const handleAxiosErrors = (err, action = "operation") => {
    let message = "An unexpected error occurred.";
    if (err.response) {
      message =
        err.response.data?.message ||
        `Failed to ${action}: ${err.response.statusText}`;
    } else if (err.request) {
      message =
        "No response from the server. Please check your internet connection.";
    } else {
      message = err.message || "Something went wrong.";
    }
    setError(message);
    toast.error(message);
    console.error(`${action} error:`, err);
  };

  // Fetch all client IDs
  const fetchClientIDs = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);
    setError(null);
    try {
      const pageSize = 50;
      const firstResponse = await axios.get(
        `${API_BASE_URL}?page=0&size=${pageSize}`,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      const { content: firstContent = [], totalPages = 1 } = firstResponse.data;
      let allClientIds = [...firstContent];

      if (totalPages > 1) {
        const promises = [];
        for (let p = 1; p < totalPages; p++) {
          promises.push(
            axios.get(`${API_BASE_URL}?page=${p}&size=${pageSize}`, {
              headers: { Authorization: `Bearer ${authToken}` },
            })
          );
        }

        const responses = await Promise.all(promises);
        responses.forEach((res) => {
          allClientIds = allClientIds.concat(res.data.content || []);
        });
      }

      setClientIDs(allClientIds);
    } catch (err) {
      handleAxiosErrors(err, "fetch client IDs");
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  // Fetch one client ID by ID
  const fetchClientIDById = useCallback(
    async (id) => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API_BASE_URL}/${id}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        setSelectedID(res.data);
        return res.data;
      } catch (err) {
        handleAxiosErrors(err, "fetch client ID details");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken]
  );

  // Update an API key
  const updateClientID = useCallback(
    async (id, updatedData) => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.put(`${API_BASE_URL}/${id}`, updatedData, {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
            "O-API-KEY": "2cfb1d79-8f6e-4313-9e3c-f15f94ea3b10",
          },
        });

        // Update local state
        setClientIDs((prev) =>
          prev.map((key) => (key.id === id ? res.data : key))
        );

        // If currently selected key matches, update it
        setSelectedID((prev) => (prev && prev.id === id ? res.data : prev));

        toast.success("Client ID updated successfully!", { theme: "colored" });
        return res.data;
      } catch (err) {
        handleAxiosErrors(err, "Update client ID");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken]
  );

  // Delete client ID
  const deleteClientID = useCallback(
    async (id) => {
      setLoading(true);
      setError(null);
      try {
        await axios.delete(`${API_BASE_URL}/${id}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });

        // Remove deleted key from local state
        setClientIDs((prev) => prev.filter((key) => key.id !== id));
        toast.success("Client ID deleted successfully!", { theme: "colored" });
      } catch (err) {
        handleAxiosErrors(err, "Delete client ID");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken]
  );

  // fetch automatically on mount
  useEffect(() => {
    if (authToken) fetchClientIDs();
  }, [authToken, fetchClientIDs]);

  return {
    clientIDs,
    selectedID,
    loading,
    error,
    fetchClientIDs,
    fetchClientIDById,
    updateClientID,
    deleteClientID,
    setSelectedID,
  };
}
